import React, { useEffect, useMemo, useState } from 'react'
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { FormItemsWrapper, OtherSpecialistWrapper, StyledForm } from './style';
import { Button, Input, notification, Radio, RadioChangeEvent } from 'antd';
import { useSelector } from 'react-redux';
import { useUpdateProfessionalClientSpecialists } from '../../../hooks/professionals/useClientGeneralInfo';
import { useUpdateClientSpecialists } from '../../../hooks/client/useClientGeneralInfo';
import { AddMoreBtnContainer } from '../../Common/Button/style';
import { RootState } from '../../../redux/store';
import { NotificationTitle } from '../../Common/Notification/style';
import { CheckCircleOutlined } from '@ant-design/icons';
import { theme } from '../../../styles/theme';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';
import { SpecialistsType } from '../../../types';

const SpecialistsForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isClient = !!user && "professionalCode" in user;
  const { update: updateProfessionalClientSpecialists, loading: loadingProfessionalClientSpecialists } = useUpdateProfessionalClientSpecialists();
  const { update: updateClientSpecialists, loading: loadingClientSpecialists } = useUpdateClientSpecialists();
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);
  const [form] = StyledForm.useForm();

  const [withSpecialist, setWithSpecialists] = useState<boolean>(Boolean(selectedClient?.specialists) ?? false);
  const [withOtherSpecialist, setWithOtherSpecialists] = useState<boolean>(Boolean(selectedClient?.otherHealthProfessionals?.length) || false);
  const otherSpecialists = useMemo(() => {
    return selectedClient?.otherHealthProfessionals.map((otherHealthProfessional: SpecialistsType) => ({
      id: otherHealthProfessional.id,
      name: otherHealthProfessional.name,
      forWhat: otherHealthProfessional.forWhat,
      sinceWhen: otherHealthProfessional.sinceWhen,
      address: otherHealthProfessional.address,
      telephoneCountryCode: otherHealthProfessional.telephoneCountryCode,
      telephoneNumber: otherHealthProfessional.telephoneNumber
    }))
  }, [selectedClient]);

  const onFinish = async (values: any) => {
    try {
      let res;
      if (isClient) {
        res = await updateClientSpecialists(values);
      } else {
        res = await updateProfessionalClientSpecialists(selectedClient?.id as string, values);
      }
      if (res || true) {
        api.info({
          message: <NotificationTitle>Client Specialist updated successfully</NotificationTitle>,
          placement: 'topRight',
          icon:
            <CheckCircleOutlined
              style={{
                color: theme.color.primary,
              }}
            />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex'
          }
        });
        return;
      }
    } catch (error: any) {
      console.error(error)
    } finally {
      refetchClient();
    }
    console.log('Received values of form: ', values);
  };

  const onChangeWithSpecialist = ({ target: { value } }: RadioChangeEvent) => {
    if (!value) {
      form.setFieldValue('_destroy', true);
    }
    setWithSpecialists(value);
  };

  const onChangeWithOtherSpecialist = ({ target: { value } }: RadioChangeEvent) => {
    if (!value) {
      const oldOtherSpecialist = form.getFieldValue('otherSpecialists') || [];
      const newOtherSpecialist = oldOtherSpecialist.map((item: any) => (
        { ...item, _destroy: true }
      ));
      form.setFieldsValue({ otherSpecialists: newOtherSpecialist });
    }
    setWithOtherSpecialists(value);
  };

  const onRemoveOtherSpecialist = (fieldIndex: any) => {
    const oldOtherSpecialist = form.getFieldValue('otherSpecialists') || [];
    const newOtherSpecialist = oldOtherSpecialist.map((item: any, index: number) =>
      index === fieldIndex ? { ...item, _destroy: true } : item
    );
    form.setFieldsValue({ otherSpecialists: newOtherSpecialist });
  };

  useEffect(() => {
    if (selectedClient?.otherHealthProfessionals.length) {
      setWithOtherSpecialists(true);
      form.setFieldValue('otherSpecialists', otherSpecialists)
    }
  }, [selectedClient, form, otherSpecialists]);

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="specialists_form"
      onFinish={onFinish}
      initialValues={{ ...selectedClient?.specialists[0] }}
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      {contextHolder}
      <p>Do you have a specialist?</p>
      <Radio.Group
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        onChange={onChangeWithSpecialist}
        value={withSpecialist}
        optionType="button"
        buttonStyle="solid"
      />
      <FormItemsWrapper hidden={!withSpecialist}>
        <StyledForm.Item
          name="id"
          hidden
        >
          <Input />
        </StyledForm.Item>
        <StyledForm.Item
          name="_destroy"
          hidden
        >
          <Input />
        </StyledForm.Item>
        <StyledForm.Item
          name="forWhat"
          label="For what?"
          labelAlign="left"
          rules={[
            {
              required: true,
              message: 'This is a required field!',
            },
          ]}
        >
          <Input />
        </StyledForm.Item>
        <StyledForm.Item
          name="sinceWhen"
          label="Since when?"
          labelAlign="left"
          rules={[
            {
              required: true,
              message: 'This is a required field!',
            },
          ]}
        >
          <Input />
        </StyledForm.Item>
        <p>Contact details</p>
        <StyledForm.Item
          name="name"
          label="Name"
          labelAlign="left"
          rules={[
            {
              required: true,
              message: 'This is a required field!',
            },
          ]}
        >
          <Input />
        </StyledForm.Item>
        <StyledForm.Item
          name="address"
          label="Address"
          labelAlign="left"
          rules={[
            {
              required: true,
              message: 'This is a required field!',
            },
          ]}
        >
          <Input />
        </StyledForm.Item>
        <StyledForm.Item
          name="telephoneCountryCode"
          label="Telephone country code"
          labelAlign="left"
          rules={[
            {
              required: true,
              message: 'This is a required field!',
            },
          ]}
        >
          <Input />
        </StyledForm.Item>
        <StyledForm.Item
          name="telephoneNumber"
          label="Telephone number"
          labelAlign="left"
          rules={[
            {
              required: true,
              message: 'This is a required field!',
            },
          ]}
        >
          <Input />
        </StyledForm.Item>
      </FormItemsWrapper>
      <p>Are there other health professionals involved?</p>
      <Radio.Group
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        onChange={onChangeWithOtherSpecialist}
        value={withOtherSpecialist}
        optionType="button"
        buttonStyle="solid"
      />
      <FormItemsWrapper hidden={!withOtherSpecialist}>
        <StyledForm.List name="otherSpecialists">
          {(fields, { add, remove }) => (
            <>
              {fields
                .filter(({ name }) => {
                  const otherSpecialists = form.getFieldValue('otherSpecialists') || [];
                  const fieldData = otherSpecialists[name];
                  return !fieldData?._destroy;
                })
                .map((field: any, index: number) => (
                  <OtherSpecialistWrapper>
                    <div className="number-container">
                      <p>{`${++index})`}</p>
                      <Button
                        size="small"
                        onClick={() => {
                          // remove(field.name);
                          onRemoveOtherSpecialist(field.name);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                    <StyledForm.Item
                      name={[field.name, 'forWhat']}
                      label="For what?"
                      labelAlign="left"
                      rules={[
                        {
                          required: true,
                          message: 'This is a required field!',
                        },
                      ]}
                    >
                      <Input />
                    </StyledForm.Item>
                    <StyledForm.Item
                      name={[field.name, 'sinceWhen']}
                      label="Since when?"
                      labelAlign="left"
                      rules={[
                        {
                          required: true,
                          message: 'This is a required field!',
                        },
                      ]}
                    >
                      <Input />
                    </StyledForm.Item>
                    <p>Contact details</p>
                    <StyledForm.Item
                      name={[field.name, 'name']}
                      label="Name"
                      labelAlign="left"
                      rules={[
                        {
                          required: true,
                          message: 'This is a required field!',
                        },
                      ]}
                    >
                      <Input />
                    </StyledForm.Item>
                    <StyledForm.Item
                      name={[field.name, 'address']}
                      label="Address"
                      labelAlign="left"
                      rules={[
                        {
                          required: true,
                          message: 'This is a required field!',
                        },
                      ]}
                    >
                      <Input />
                    </StyledForm.Item>
                    <StyledForm.Item
                      name={[field.name, 'telephoneCountryCode']}
                      label="Telephone country code"
                      labelAlign="left"
                      rules={[
                        {
                          required: true,
                          message: 'This is a required field!',
                        },
                      ]}
                    >
                      <Input />
                    </StyledForm.Item>
                    <StyledForm.Item
                      name={[field.name, 'telephoneNumber']}
                      label="Telephone number"
                      labelAlign="left"
                      rules={[
                        {
                          required: true,
                          message: 'This is a required field!',
                        },
                      ]}
                    >
                      <Input />
                    </StyledForm.Item>
                  </OtherSpecialistWrapper>
                ))}
              <AddMoreBtnContainer>
                <Button
                  size="small"
                  onClick={() => add()}
                >
                  Add more
                </Button>
              </AddMoreBtnContainer>
            </>
          )}
        </StyledForm.List>
      </FormItemsWrapper>
      <StyledForm.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button type="primary" htmlType="submit" loading={isClient ? loadingClientSpecialists : loadingProfessionalClientSpecialists}>
          Save
        </Button>
      </StyledForm.Item>
    </StyledForm>
  )
}

export default SpecialistsForm;